<template>
    <div class="vx-row">
        <div class="w-full">
            <div class="pt-4">
                <!--
                    Two responsive modes driven by isNarrow (ResizeObserver on
                    layoutContainer):

                    WIDE  (≥ 1094 px): 3 columns side-by-side
                      [controls col] | [phone preview] | [form]

                    NARROW (< 1094 px): toolbar above + phone/form side-by-side
                      [Create | Language | Toggles | Cancel Save]
                      [phone preview]  |  [form]
                -->
                <div
                    ref="layoutContainer"
                    :class="['flex gap-8', isNarrow ? 'flex-col' : 'flex-row items-stretch']"
                >
                    <!-- Controls: vertical column in wide, horizontal toolbar in narrow -->
                    <div :class="isNarrow
                        ? 'flex flex-row flex-wrap items-center gap-x-6 gap-y-2'
                        : 'flex-shrink-0 flex flex-col'"
                        :style="isNarrow ? '' : 'min-width: 160px'"
                    >
                        <!-- Create item (wide: top | narrow: right via order:3 + ml-auto) -->
                        <button
                            v-if="canBeEdited"
                            @click="onCreate({ insideItemUuid: createInsidePath })"
                            :class="['rounded-lg sk-btn sk-btn-primary', isNarrow ? 'ml-auto' : 'w-full']"
                            :style="isNarrow ? { order: 3 } : {}"
                        >
                            {{ $t('apps.configurator.menu-list.create-item') }}
                        </button>

                        <!-- Language selector + visibility toggles -->
                        <div :class="isNarrow
                            ? 'flex flex-row flex-wrap items-center gap-x-6 gap-y-2'
                            : 'flex flex-col gap-3'"
                            :style="isNarrow ? { order: 2 } : { marginTop: '24px' }"
                        >
                            <vs-select
                                v-if="languages.length > 1"
                                v-model="selectedLanguageAcronym"
                                class="border-grey-light"
                                style="min-width: 90px;"
                            >
                                <vs-select-item
                                    v-for="lang in languages"
                                    :key="lang.acronym"
                                    :value="lang.acronym"
                                    :text="lang.name"
                                />
                            </vs-select>
                            <vs-checkbox v-model="showUnpublished">
                                {{ $t('apps.configurator.menu-list.show-unpublished') }}
                            </vs-checkbox>
                            <vs-checkbox v-if="showGuidesAvailable" v-model="showGuides">
                                {{ $t('apps.configurator.menu-list.show-guides') }}
                            </vs-checkbox>
                        </div>

                        <!-- Cancel + Save (wide: bottom via margin-top:auto | narrow: left via order:1) -->
                        <div
                            v-if="canBeEdited"
                            :class="['flex gap-2', isNarrow ? 'flex-row' : 'flex-col']"
                            :style="isNarrow ? { order: 1 } : { marginTop: 'auto' }"
                        >
                            <button
                                @click="cancel"
                                :disabled="!isDirty"
                                class="rounded-lg sk-btn sk-btn-primary btn-border"
                            >
                                {{ $t('apps.configurator.menu-list.cancel') }}
                            </button>
                            <button
                                @click="save"
                                :disabled="!isDirty"
                                class="rounded-lg sk-btn sk-btn-primary"
                            >
                                {{ $t('apps.configurator.menu-list.save') }}
                            </button>
                        </div>
                    </div>

                    <!-- Phone preview + form: always side-by-side regardless of mode.
                         In narrow mode this inner row fills the full container width. -->
                    <div class="flex flex-row flex-1 items-stretch gap-8">

                        <!-- Phone preview — natural width -->
                        <div class="flex-shrink-0" ref="gridCol">
                            <component
                                :is="editorComponentName"
                                ref="menuEditor"
                                :menu="menu"
                                :version="version"
                                :readOnly="!canBeEdited"
                                :show-unpublished="showUnpublished"
                                :show-guides="showGuides"
                                :selected-language-acronym="selectedLanguageAcronym"
                                :editing-item-uuid="editingItemUuid"
                                @editItem="editItem"
                                @deleteItem="deleteItem"
                                @navigationChanged="onNavigationChanged"
                            />
                        </div>

                        <!-- Form — takes all remaining horizontal space -->
                        <div
                            class="flex-1 min-w-0 flex flex-col overflow-hidden"
                            :style="formColStyle"
                        >
                            <ManageMenuItemForm
                                v-show="isEditing"
                                ref="manageMenuItemForm"
                                :languages="version.languages"
                                :layout="version.layoutByType(menu.type)"
                                :templates="activeTemplates"
                                :menu="menu"
                                :resources="version.resourcesByPlatformAndTimeofyear('android', menu.timeofyear)"
                                @close="closeSession"
                                @loaded="$emit('nodeOpenChanged')"
                                :readOnly="!canBeEdited"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Version } from '@/modules/Apps/Models/Version'
import MenuGrid from '@/modules/Apps/Components/configurator/MenuGrid.vue'
import MenuTabsEditor from '@/modules/Apps/Components/configurator/MenuTabsEditor.vue'
import ManageMenuItemForm from '@/modules/Apps/Components/configurator/ManageMenuItemForm.vue'
import ConfiguratorMenuService from '@/modules/Apps/Services/Configurator/ConfiguratorMenuService'
import loader from '@/modules/Shared/Mixins/loader'
import i18n from '@/i18n'
import { userHasAdminRole } from '@/modules/Auth/Helpers/permissionsHelper'
import {
    isNativeSubmenuItem,
    filterTemplatesForContext,
    CONTEXT_KINDS,
    MENU_TYPES,
} from '@/modules/Apps/Helpers/menuVisuals'
import {
    buildItemsByUuid,
    getMenuTemplates,
    recomputeOrdering,
} from '@/modules/Apps/Helpers/menuContext'
import { v4 as uuidv4 } from 'uuid'

// Empty-item shape used when creating a new menu item — fields match the API
// schema so the form can bind to it without touching defaults.
function createDefaultItem() {
    return {
        uuid: uuidv4(),
        name: '',
        configuration: null,
        flag: '',
        icon: '',
        item_template_uuid: null,
        published: true,
        screen_name: '',
        screen_name_category: null,
        section: '',
        submenu_items: [],
        titles: {},
        width: 1,
    }
}

// Recursively insert `newItem` as the first child of the item with `parentUuid`.
function addItemInside(items, parentUuid, newItem) {
    for (let i = 0; i < items.length; i++) {
        if (items[i].uuid === parentUuid) {
            items[i].submenu_items.push(newItem)
            return true
        }
        if (items[i].submenu_items && addItemInside(items[i].submenu_items, parentUuid, newItem)) {
            return true
        }
    }
    return false
}

// Recursively insert `newItem` immediately after the item with `afterUuid`.
function addItemAfter(items, afterUuid, newItem) {
    for (let i = 0; i < items.length; i++) {
        if (items[i].uuid === afterUuid) {
            items.splice(i + 1, 0, newItem)
            return true
        }
        if (items[i].submenu_items && addItemAfter(items[i].submenu_items, afterUuid, newItem)) {
            return true
        }
    }
    return false
}

// Decorative card styling for the form column (only when an edit/create session is open).
const FORM_CARD_DECORATION = {
    background: '#F4F4FA',
    border: '1px solid rgba(0,0,0,0.12)',
    'border-radius': '12px',
    'box-shadow': '0 2px 12px rgba(0,0,0,0.06)',
    padding: '0 20px',
}

/**
 * editSession state model
 * ───────────────────────
 * Single source of truth for the edit/create form lifecycle. Replaces the
 * previous fragmented state (action + editingItemUuid + manageMenuItemFormProps
 * + activeTemplates) which had to be kept in sync manually.
 *
 *   null                                          → no form open ('list' mode)
 *   { mode: 'edit',   itemUuid,   allowedTemplates }  → editing an existing item
 *   { mode: 'create', insideItemUuid?, allowedTemplates } → creating a new item
 */
const SESSION_MODES = Object.freeze({ EDIT: 'edit', CREATE: 'create' })

export default {
    name: 'MenuCollapseItemContent',

    mixins: [loader],

    components: { MenuGrid, MenuTabsEditor, ManageMenuItemForm },

    props: {
        version: { type: Version, required: true },
        menu:    { type: Object,  required: true },
    },

    data() {
        return {
            editSession: null,
            showUnpublished: true,
            showGuides: false,
            // Active language for label/icon rendering — drives the editor display.
            // Defaults to the version's main language in mounted().
            selectedLanguageAcronym: null,
            currentPathUuids: [],
            gridColHeight: 0,
            // Snapshot of menu.items at the last "clean" point (initial mount, after
            // save, or after cancel). Used by `isDirty` to detect unsaved changes.
            originalMenuItemsJson: null,
            // Measured width of the layout container. Drives isNarrow.
            containerWidth: 0,
        }
    },

    computed: {
        canBeEdited() {
            return !this.version.isPublished() || (this.version.isPublished() && userHasAdminRole())
        },
        editorComponentName() {
            return this.menu.type === MENU_TYPES.TAB ? 'MenuTabsEditor' : 'MenuGrid'
        },
        // The languages dropdown reads from version.languages. Hidden when there's
        // only one language (no choice to make).
        languages() { return this.version.languages },
        // Tab menus don't render a grid → no guides to overlay.
        showGuidesAvailable() { return this.menu.type !== MENU_TYPES.TAB },
        // Resolves the next "create item" insertion target: when navigated inside
        // a submenu, the new item should land inside that submenu.
        createInsidePath() {
            return this.currentPathUuids.length > 0
                ? this.currentPathUuids[this.currentPathUuids.length - 1]
                : undefined
        },
        // O(1) UUID → item lookup (fixes the previous Array.find-recursion bug)
        itemsByUuid() { return buildItemsByUuid(this.menu) },
        currentContextKind() {
            if (this.currentPathUuids.length === 0) return CONTEXT_KINDS.ROOT
            const parentUuid = this.currentPathUuids[this.currentPathUuids.length - 1]
            const parent = this.itemsByUuid[parentUuid]
            return (parent && isNativeSubmenuItem(parent)) ? CONTEXT_KINDS.NATIVE_SUBMENU : CONTEXT_KINDS.HOME_SUBMENU
        },
        allTemplates() { return getMenuTemplates(this.menu, this.version) },

        // ───── Derived from editSession ─────

        isEditing() { return this.editSession !== null },
        // Kept for backwards compatibility with downstream code expecting 'list'|'form'.
        action() { return this.isEditing ? 'form' : 'list' },
        // The uuid of the cell being edited. With live binding, create-mode
        // sessions also point to a real item (the freshly-inserted placeholder),
        // so the editing highlight applies to both modes.
        editingItemUuid() {
            return this.editSession ? this.editSession.itemUuid : null
        },
        // Templates passed to the form: filtered on create, full on edit.
        activeTemplates() {
            return this.editSession ? this.editSession.allowedTemplates : this.allTemplates
        },

        // ───── Form-column styles ─────

        formColHeightStyle() {
            return this.gridColHeight ? { height: `${this.gridColHeight}px` } : {}
        },
        formColDecorationStyle() {
            return this.isEditing ? FORM_CARD_DECORATION : {}
        },
        formColStyle() {
            return Object.assign({}, this.formColHeightStyle, this.formColDecorationStyle)
        },

        // True when the container is too narrow to fit all three columns side-by-side
        // (controls 160 + gap 32 + phone 390 + ml 32 + form 480 = 1094 px).
        // Switches to toolbar-above layout automatically when the sidebar is
        // expanded or the browser window is below the threshold.
        isNarrow() {
            return this.containerWidth > 0 && this.containerWidth < 1094
        },

        // True when the current items tree diverges from the last clean snapshot.
        // Drives the "unsaved changes" indicator on the Save button.
        isDirty() {
            if (this.originalMenuItemsJson === null) return false
            return JSON.stringify(this.menu.items) !== this.originalMenuItemsJson
        },
    },

    watch: {
        // When the parent swaps to a different menu without unmounting, the new
        // menu's items become the new clean baseline.
        'menu.uuid'() {
            this.captureCleanSnapshot()
        },
    },

    mounted() {
        // Capture the initial clean state — anything that diverges from this
        // marks the menu as dirty until the user saves or cancels.
        this.captureCleanSnapshot()

        // Initialize language selector to the version's main language.
        const main = this.version.mainLanguage()
        this.selectedLanguageAcronym = main ? main.acronym : null

        // ResizeObserver mirrors the grid column height into the form column.
        // requestAnimationFrame-throttled to coalesce rapid resize events.
        this._gridColRAF = null
        this._gridColRO = new ResizeObserver(entries => {
            const height = entries[0] && entries[0].contentRect.height
            if (!(height >= 0)) return
            if (this._gridColRAF !== null) return
            this._gridColRAF = requestAnimationFrame(() => {
                this._gridColRAF = null
                this.gridColHeight = height
            })
        })
        if (this.$refs.gridCol) this._gridColRO.observe(this.$refs.gridCol)

        // ResizeObserver on the outer layout container — drives isNarrow so the
        // layout switches between 3-column and toolbar-above automatically when
        // the sidebar expands/collapses or the window is resized.
        this._containerRAF = null
        this._containerRO = new ResizeObserver(entries => {
            const width = entries[0] && entries[0].contentRect.width
            if (!(width > 0)) return
            if (this._containerRAF !== null) return
            this._containerRAF = requestAnimationFrame(() => {
                this._containerRAF = null
                this.containerWidth = width
            })
        })
        if (this.$refs.layoutContainer) this._containerRO.observe(this.$refs.layoutContainer)
    },

    beforeDestroy() {
        if (this._gridColRAF !== null) cancelAnimationFrame(this._gridColRAF)
        if (this._gridColRO) this._gridColRO.disconnect()
        if (this._containerRAF !== null) cancelAnimationFrame(this._containerRAF)
        if (this._containerRO) this._containerRO.disconnect()
    },

    methods: {
        // ───── Session lifecycle ─────

        async onCreate(payload) {
            // Live-binding model: the new item is inserted into menu.items
            // immediately so the form can bind to it by reference. If the user
            // closes the form before picking a template, closeSession will roll
            // back the empty item (see below).
            //
            // Undo: push the pre-create snapshot now. One Cmd+Z reverts the
            // entire "create + configure" arc. If the user abandons the create
            // without picking a template, closeSession removes this snapshot
            // again (so the stack stays clean).
            const createSnapshot = this.$refs.menuEditor
                ? this.$refs.menuEditor.pushUndoSnapshot()
                : null
            const newItem = createDefaultItem()
            const insideItemUuid = payload && payload.insideItemUuid
            const afterItemUuid  = payload && payload.afterItemUuid
            if (insideItemUuid) {
                addItemInside(this.menu.items, insideItemUuid, newItem)
            } else if (afterItemUuid) {
                addItemAfter(this.menu.items, afterItemUuid, newItem)
            } else {
                this.menu.items.push(newItem)
            }
            recomputeOrdering(this.menu.items)
            this.editSession = {
                mode: SESSION_MODES.CREATE,
                itemUuid: newItem.uuid,
                allowedTemplates: filterTemplatesForContext(this.allTemplates, this.currentContextKind),
                createSnapshot, // ref for potential rollback in closeSession
            }
            await this.$refs.manageMenuItemForm.initialize(newItem, 'create')
        },
        async editItem(item) {
            this.editSession = {
                mode: SESSION_MODES.EDIT,
                itemUuid: item.uuid,
                // Edit keeps ALL templates so legacy items can be re-saved (tolerance).
                allowedTemplates: this.allTemplates,
                // Capture pre-session items as a string for fast equality on close.
                // If anything changed during the session, this string differs from
                // the post-edit JSON and a snapshot is pushed on closeSession.
                presessionItemsJson: JSON.stringify(this.menu.items),
            }
            // Pass the actual reactive item — form mutates it directly (live binding).
            await this.$refs.manageMenuItemForm.initialize(item, 'edit')
        },
        closeSession() {
            const session = this.editSession
            if (session) {
                if (session.mode === SESSION_MODES.CREATE) {
                    // Roll back an abandoned create: empty placeholder + its
                    // undo snapshot disappear together.
                    const item = this.itemsByUuid[session.itemUuid]
                    if (item && !item.item_template_uuid) {
                        this.removeItemFromTree(item, this.menu.items)
                        recomputeOrdering(this.menu.items)
                        if (session.createSnapshot && this.$refs.menuEditor) {
                            this.$refs.menuEditor.removeUndoSnapshot(session.createSnapshot)
                        }
                    }
                    // If a template WAS picked, the create snapshot stays in the
                    // stack — one Cmd+Z reverts the whole create+configure.
                } else if (session.mode === SESSION_MODES.EDIT) {
                    // Push the pre-session snapshot only if something actually
                    // changed during the session.
                    if (session.presessionItemsJson) {
                        const now = JSON.stringify(this.menu.items)
                        if (now !== session.presessionItemsJson && this.$refs.menuEditor) {
                            this.$refs.menuEditor.pushUndoSnapshot(
                                JSON.parse(session.presessionItemsJson),
                            )
                        }
                    }
                }
            }
            this.editSession = null
            this.$emit('nodeOpenChanged')
        },

        // ───── Grid wiring ─────

        onNavigationChanged(pathUuids) {
            this.currentPathUuids = pathUuids
            this.$emit('nodeOpenChanged')
        },
        deleteItem(item) {
            // Capture pre-delete snapshot so Cmd+Z can restore the removed item.
            if (this.$refs.menuEditor) this.$refs.menuEditor.pushUndoSnapshot()
            this.removeItemFromTree(item, this.menu.items)
        },
        removeItemFromTree(itemToDelete, itemsList) {
            const idx = itemsList.findIndex(i => i.uuid === itemToDelete.uuid)
            if (idx !== -1) {
                itemsList.splice(idx, 1)
                return
            }
            itemsList.forEach(item => {
                this.removeItemFromTree(itemToDelete, item.submenu_items || [])
            })
        },

        // ───── Save / Cancel (full menu PATCH) ─────

        captureCleanSnapshot() {
            this.originalMenuItemsJson = JSON.stringify(this.menu.items)
        },
        cancel() {
            // Restore from the in-memory snapshot — no API round-trip needed since
            // the baseline already holds the last clean state.  The snapshot stays
            // valid: parsed items match it byte-for-byte, so isDirty flips back
            // to false automatically.
            if (this.originalMenuItemsJson === null) return
            this.menu.items = JSON.parse(this.originalMenuItemsJson)
            // Close any open form — its internalItem now points to an orphaned
            // object that's no longer in menu.items.
            this.editSession = null
        },
        async save() {
            await this.load(async () => {
                try {
                    await ConfiguratorMenuService.editMenu(this.menu.uuid, {
                        ...this.menu,
                        resorts: this.menu.resorts,
                        version_uuid: this.version.version.uuid,
                    })
                    this.notifySuccess()
                    // Reset the dirty baseline — the just-saved state is the new clean.
                    this.captureCleanSnapshot()
                } catch (error) {
                    if (error.response && error.response.status === 422) {
                        this.handleValidationErrors(error.response.data)
                        return
                    }
                    this.notifyError()
                }
            })
        },
        handleValidationErrors(responseData) {
            const messages = JSON.parse(responseData.description)
            const keys = ['ITEM', 'RESORTS', 'ITEM_TEMPLATE', 'TITLE']
            keys.forEach(key => {
                if (!messages[key]) return
                let details = messages[key]
                if (key === 'ITEM') details = details.CONFIGURATION
                details.forEach(message => {
                    this.notifyError(i18n.t('shops.error.0'), this.processErrorMessage(key, message), 7000)
                })
            })
        },

        findTemplate(uuid) {
            return this.allTemplates.find(t => t.uuid === uuid)
        },
        findMenuItem(uuid) {
            return this.itemsByUuid[uuid] || null
        },
        processErrorMessage(key, message) {
            if (key !== 'ITEM') return message
            const uuidRegex = /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/g
            const matches = message.match(uuidRegex) || []
            const [itemUuid, templateUuid] = matches
            const itemTitles = (this.findMenuItem(itemUuid) || {}).titles || {}
            const tmpl = this.findTemplate(templateUuid)
            if (itemUuid && itemTitles[this.version.mainLanguage().acronym]) {
                message = message.replace(itemUuid, itemTitles[this.version.mainLanguage().acronym])
            }
            if (templateUuid && tmpl && tmpl.name && tmpl.name.scalar) {
                message = message.replace(templateUuid, i18n.t(tmpl.name.scalar))
            }
            return message.replace('item_template', 'template')
        },
    },
}
</script>
